import type { User } from "firebase/auth";
import type {
  ActionJobKind,
  CategoryConfig,
  DashboardCollections,
  DashboardViews,
  HistoryPoint,
  Movement,
  ReconciliationRecord,
  SavingsBucket,
  TemporaryContribution,
  TemporaryContributionEvent,
} from "./types";
import { runFirebaseAction } from "./firebase-actions";

const emptyCollections = (): DashboardCollections => ({
  movements: [],
  categories: [],
  savingsBuckets: [],
  weeklyAdjustments: [],
  cardPaymentEvents: [],
  pendingCredits: [],
  history: [],
  reconciliations: [],
  temporaryContributions: [],
  temporaryContributionEvents: [],
});

function excelDateToIso(value: unknown): string | undefined {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return undefined;
  return new Date(Date.UTC(1899, 11, 30) + Math.floor(numeric) * 86_400_000).toISOString().slice(0, 10);
}

function cents(value: unknown): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.round(numeric * 100) : 0;
}

function normalizeHistory(item: Record<string, unknown>): HistoryPoint {
  return {
    id: String(item.id ?? crypto.randomUUID()),
    period: String(item.period ?? item["Quincena"] ?? "Quincena"),
    start: String(item.start ?? "") || excelDateToIso(item["Inicio"]),
    end: String(item.end ?? "") || excelDateToIso(item["Fin"]),
    totalMoneyCents: item.totalMoneyCents == null ? cents(item["Dinero total al cierre"]) : Number(item.totalMoneyCents),
    savingsCents: item.savingsCents == null ? cents(item["Ahorro acumulado al cierre"]) : Number(item.savingsCents),
    spentCents: item.spentCents == null ? cents(item["Total gastado"]) : Number(item.spentCents),
    priorExpenseAdjustmentCents: Number(item.priorExpenseAdjustmentCents ?? 0),
    spentPercent: Number(item.spentPercent ?? item["Porcentaje gastado"] ?? 0),
  };
}

function normalizeReconciliation(item: Record<string, unknown>): ReconciliationRecord {
  return {
    id: String(item.id ?? crypto.randomUUID()),
    checkedAt: String(item.checkedAt ?? "") || excelDateToIso(item["Fecha comprobación"]),
    account: String(item.account ?? item["Cuenta"] ?? "NU"),
    appBalanceCents: item.appBalanceCents == null ? cents(item["Saldo app"]) : Number(item.appBalanceCents),
    realBalanceCents: item.realBalanceCents == null ? cents(item["Saldo real"]) : Number(item.realBalanceCents),
    differenceCents: item.differenceCents == null ? cents(item["Diferencia"]) : Number(item.differenceCents),
    status: String(item.status ?? item["Estado"] ?? "PENDIENTE"),
  };
}

export async function subscribeToDashboardCollections(
  uid: string,
  onValue: (value: DashboardCollections) => void,
  onError: (error: Error) => void,
): Promise<() => void> {
  const [{ collection, onSnapshot }, { firestore }] = await Promise.all([
    import("firebase/firestore"),
    import("./firebase"),
  ]);
  const value = emptyCollections();
  const unsubscribers: Array<() => void> = [];
  const subscribe = <T>(name: string, assign: (items: T[]) => void) => {
    unsubscribers.push(onSnapshot(collection(firestore, "users", uid, name), (documents) => {
      assign(documents.docs.map((item) => ({ id: item.id, ...item.data() })) as T[]);
      onValue({ ...value });
    }, onError));
  };

  subscribe<Movement>("movements", (items) => {
    value.movements = items.sort((a, b) => String(b.spentOn ?? b.createdAt).localeCompare(String(a.spentOn ?? a.createdAt)));
  });
  subscribe<CategoryConfig>("categories", (items) => {
    value.categories = items.filter((item) => item.active !== false).sort((a, b) => Number(a.order) - Number(b.order));
  });
  subscribe<SavingsBucket>("savingsBuckets", (items) => {
    value.savingsBuckets = items.filter((item) => item.active !== false).sort((a, b) => Number(a.order) - Number(b.order));
  });
  subscribe<Record<string, unknown>>("weeklyAdjustments", (items) => { value.weeklyAdjustments = items; });
  subscribe<import("../functions/card-reminder-logic.js").PaymentDeadline>("cardPaymentReminders", items => { value.cardPaymentReminders = items; });
  subscribe<Record<string, unknown>>("cardPaymentEvents", (items) => { value.cardPaymentEvents = items; });
  subscribe<Record<string, unknown>>("pendingCredits", (items) => { value.pendingCredits = items; });
  subscribe<Record<string, unknown>>("periodHistory", (items) => {
    value.history = items.map(normalizeHistory).sort((a, b) => String(a.start).localeCompare(String(b.start)));
  });
  subscribe<Record<string, unknown>>("reconciliations", (items) => {
    value.reconciliations = items.map(normalizeReconciliation).sort((a, b) => String(b.checkedAt).localeCompare(String(a.checkedAt)));
  });
  subscribe<TemporaryContribution>("temporaryContributions", (items) => {
    value.temporaryContributions = items
      .sort((a, b) => Number(a.order) - Number(b.order));
  });
  subscribe<TemporaryContributionEvent>("temporaryContributionEvents", (items) => {
    value.temporaryContributionEvents = items
      .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
  });
  return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
}

export async function runDashboardAction(
  user: User,
  kind: ActionJobKind,
  payload: Record<string, unknown> = {},
): ReturnType<typeof runFirebaseAction> {
  return runFirebaseAction(user, kind, payload);
}

export async function subscribeToDashboardViews(
  uid: string,
  onValue: (views: DashboardViews) => void,
  onError: (error: Error) => void,
): Promise<() => void> {
  const [{ collection, onSnapshot }, { firestore }] = await Promise.all([
    import("firebase/firestore"),
    import("./firebase"),
  ]);
  return onSnapshot(collection(firestore, "users", uid, "views"), (documents) => {
    const views: DashboardViews = {};
    documents.docs.forEach((document) => { views[document.id] = document.data() as DashboardViews[string]; });
    onValue(views);
  }, onError);
}
